import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { CustomInput } from '../../../components/common';
import { decimalOnChange, numberonBlur, numberOnChange, onBlur } from '../../../components/common/useForm';
import AccordionTile from '../Accordion';

import { Package, Subscription } from '../../../hooks/useClients';
import {
  useCreateSubscription,
  useDefaultSubscriptions,
  useUpdateSubscription,
  Subscription as SubscriptionType,
  useRemoveSubscription,
} from '../../../hooks/useSubscriptions';
import { UseMutationResult } from '@tanstack/react-query';

enum SwitchBarSuffix {
  MORE_DETAILS = 'More Details',
  TITLE = 'title',
}

enum SwitchBarType {
  DETAILS = 'details',
  DESCRIPTION = 'description',
}

enum SubscriptionsType {
  EXISTING = 'existing',
  NEW = 'new',
}

interface MemberPayProps {
  packageData: Package;
}

const MemberPay: React.FunctionComponent<MemberPayProps> = ({ packageData }): JSX.Element => {
  const { data: defaultSubscriptions } = useDefaultSubscriptions();
  const { createSubscription } = useCreateSubscription(packageData.client_id!);
  const { updateSubscription } = useUpdateSubscription(packageData.client_id!);
  const [memberPay, setMemberPay] = useState<boolean>(packageData?.subscriptions?.length > 0);

  const generateHash = () => {
    var characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    var hash = '';

    for (var i = 0; i < 6; i++) {
      var randomIndex = Math.floor(Math.random() * characters.length);
      hash += characters.charAt(randomIndex);
    }

    return hash;
  };

  const handleDefaultTemplate = (isStatus: boolean) => {
    setMemberPay(isStatus);
    if (isStatus && !packageData?.subscriptions?.length) {
      defaultSubscriptions?.map((item: SubscriptionType, index: number) => {
        createSubscription.mutate({
          payload: {
            ...item,
            discount: item?.discount ? item?.discount : 0,
            package_id: packageData?.id,
            code: generateHash(),
          },
          bypassToast: true,
          byPassInvalidateQuery: defaultSubscriptions?.length === index + 1 ? false : true,
        });
      });
    }
  };

  return (
    <AccordionTile
      setStatus={(isStatus) => {
        handleDefaultTemplate(isStatus);
      }}
      Label={'MEMBER PAY'}
      status={memberPay}
      disableButton={true}
      id={'MemberPay'}
      buttonText={''}
      initialstatus={true}
      InnerComponent={
        <div>
          <h6 className="text-md text-gray-dark mt-4 font-bold uppercase">Options</h6>
          <div className="my-5 w-full justify-between gap-7">
            {packageData?.subscriptions.length > 0 &&
              packageData?.subscriptions
                .sort((a: Subscription, b: Subscription) => a.id - b.id)
                .map((item: Subscription) => (
                  <Subscriptions
                    data={item}
                    key={item.id}
                    id={packageData.id!}
                    type={SubscriptionsType.EXISTING}
                    createSubscription={createSubscription}
                    updateSubscription={updateSubscription}
                  />
                ))}
          </div>
          <Subscriptions
            data={{
              minutes: 0,
              code: '',
              discount: 0,
              price: 0,
              cents_per_minute: 1,
              banner_text: '',
              name: '',
              package_id: packageData.id!,
            }}
            key={'new'}
            id={packageData.id!}
            type={SubscriptionsType.NEW}
            createSubscription={createSubscription}
            updateSubscription={updateSubscription}
          />
          <div>
            {packageData?.subscriptions.length > 0 && (
              <div className="mt-8">
                <SwitchBar
                  data={packageData.subscriptions.sort((a: Subscription, b: Subscription) => a.id - b.id)}
                  type={SwitchBarType.DESCRIPTION}
                  suffix={SwitchBarSuffix.TITLE}
                  updateSubscription={updateSubscription}
                />
              </div>
            )}
          </div>
          <div>
            {packageData?.subscriptions.length > 0 && (
              <div className="mt-8">
                <SwitchBar
                  data={packageData.subscriptions.sort((a: Subscription, b: Subscription) => a.id - b.id)}
                  type={SwitchBarType.DETAILS}
                  suffix={SwitchBarSuffix.MORE_DETAILS}
                  updateSubscription={updateSubscription}
                />
              </div>
            )}
          </div>
        </div>
      }
    />
  );
};
interface SwitchBarProps {
  type: SwitchBarType;
  data: Subscription[];
  suffix: SwitchBarSuffix;
  updateSubscription: UseMutationResult<
    SubscriptionType,
    unknown,
    { subscription_id: number; payload: Partial<SubscriptionType> },
    unknown
  >;
}

type SwitchBarState = {
  position: number;
  text: string;
  id: number;
  dataModified: boolean;
};

const SwitchBar: React.FunctionComponent<SwitchBarProps> = ({
  type,
  data,
  suffix,
  updateSubscription,
}): JSX.Element => {
  const [state, setState] = useState<SwitchBarState>({ position: 0, text: '', id: 0, dataModified: false });

  const handleTab = (position: number, text: string, id: number) => {
    setState({ position, text, dataModified: false, id });
  };

  const handleState = (evt: React.ChangeEvent<HTMLTextAreaElement>) => {
    setState((prev) => ({ ...prev, text: evt.target.value, dataModified: true }));
  };

  useEffect(() => {
    if (data && type) {
      const sub = data[0];
      setState({ position: 0, text: sub[type]!, id: sub?.id, dataModified: false });
    }
  }, [data, type]);

  const update = () => {
    const payload = { [type]: state.text };
    updateSubscription.mutate({ subscription_id: state?.id, payload });
  };

  return (
    <>
      <div className="flex h-full items-center justify-start">
        {data.map((item: Subscription, index: number) => (
          <button
            className={
              'px-5 py-2 text-sm ' +
              (index === state.position
                ? ' bg-blue-primary h-full font-bold text-white'
                : ' text-gray-dark bg-gray-background-light h-full')
            }
            key={item.id}
            //@ts-ignore
            onClick={() => {
              handleTab(index, item[type]!, item.id);
            }}
          >
            {item.name}-{suffix}
          </button>
        ))}
      </div>
      <textarea
        value={state.text || ''}
        className="text-gray-dark bg-gray-background-light h-28 w-full rounded px-3 py-2 text-sm font-extrabold leading-4 focus:outline-none"
        onChange={handleState}
      />
      <button
        className={`h-10 rounded-full px-3 py-1 text-center text-sm text-white ${
          state.dataModified ? 'bg-blue-primary' : 'bg-gray-background-light'
        }`}
        onClick={update}
      >
        Update
      </button>
    </>
  );
};

interface SubscriptionsProps {
  data: Partial<SubscriptionType>;
  id: number;
  type: SubscriptionsType;
  createSubscription: UseMutationResult<
    SubscriptionType,
    unknown,
    {
      payload: Partial<SubscriptionType>;
      bypassToast: boolean;
      byPassInvalidateQuery: boolean;
    },
    unknown
  >;
  updateSubscription: UseMutationResult<
    SubscriptionType,
    unknown,
    { subscription_id: number; payload: Partial<SubscriptionType> },
    unknown
  >;
}

const Subscriptions: React.FunctionComponent<SubscriptionsProps> = ({
  data,
  id,
  type = SubscriptionsType.EXISTING,
  createSubscription,
  updateSubscription,
}): JSX.Element => {
  const clientId = localStorage.getItem('clientid');
  const { removeSubscription } = useRemoveSubscription(Number(clientId!));

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: useMemo(() => {
      if (data) {
        return {
          ...data,
          price: data.price! / 100,
          cents_per_minute: data.cents_per_minute! / 100,
        };
      }
    }, [data]),
  });

  useEffect(() => {
    if (data) {
      reset({
        ...data,
        price: data.price! / 100,
        cents_per_minute: data.cents_per_minute! / 100,
      });
    }
  }, [data]);

  const remove = (e: any) => {
    e.preventDefault();
    removeSubscription.mutate({ subscription_id: data.id });
  };

  const onSubmit = (data: any) => {
    const payload = {
      ...data,
      cents_per_minute: data.cents_per_minute * 100,
      price: data.price * 100,
      package_id: id,
    };
    if (type === SubscriptionsType.NEW) {
      createSubscription.mutate({
        payload,
        bypassToast: false,
        byPassInvalidateQuery: false,
      });
    } else {
      updateSubscription.mutate({ subscription_id: data.id, payload });
    }
  };

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="my-4 grid w-full grid-cols-8 gap-4">
          <div className="w-full">
            <CustomInput
              inputType="text"
              Label="Name"
              register={{
                ...register('name', {
                  required: 'Please provide Subscription name',
                  onBlur: (e: any) => onBlur(e, 'name', setValue),
                }),
              }}
              //@ts-ignore
              Error={errors?.name?.message?.length ? true : false}
              ErrorMessage={errors.name?.message}
              dataTestId="subscription-name"
            />
          </div>
          <div className="w-full">
            <CustomInput
              inputType="text"
              Label="Code"
              register={{
                ...register('code', {
                  required: 'Please provide Subscription code',
                  onBlur: (e: any) => onBlur(e, 'code', setValue),
                }),
              }}
              //@ts-ignore
              Error={errors?.code?.message?.length ? true : false}
              ErrorMessage={errors.code?.message}
              dataTestId="subscription-code"
            />
          </div>

          <div className="w-full">
            <CustomInput
              inputType="text"
              Label="Minutes"
              register={{
                ...register('minutes', {
                  required: 'Please provide Subscription minutes',
                  onChange: (e) => numberOnChange(e, 'minutes', setValue),
                  onBlur: (e) => numberonBlur(e, 'minutes', setValue),
                  valueAsNumber: true,

                  min: {
                    value: 0,
                    message: 'Enter 0 (zero) or more',
                  },
                }),
              }}
              //@ts-ignore
              Error={errors?.minutes?.message?.length ? true : false}
              ErrorMessage={errors.minutes?.message}
              dataTestId="subscription-minutes"
            />
          </div>
          <div className="w-full">
            <CustomInput
              inputType="text"
              Label="Price ($)"
              register={{
                ...register('price', {
                  required: 'Please provide Subscription price',
                  onChange: (e) => decimalOnChange(e, 'price', setValue, 2),
                  onBlur: (e) => numberonBlur(e, 'price', setValue),
                  valueAsNumber: true,
                  min: {
                    value: 0.0,
                    message: 'Enter 0 (zero) or more',
                  },
                }),
              }}
              //@ts-ignore
              Error={!!errors?.price?.message?.length}
              ErrorMessage={errors.price?.message}
              enableAdornment
              dataTestId="subscription-price"
            />
          </div>
          <div className="w-full">
            <CustomInput
              inputType="text"
              Label="Overage ($/min)"
              register={{
                ...register('cents_per_minute', {
                  required: 'Please provide Subscription Overage ($/min)',
                  onChange: (e) => decimalOnChange(e, 'cents_per_minute', setValue, 2),
                  onBlur: (e) => numberonBlur(e, 'cents_per_minute', setValue),
                  valueAsNumber: true,
                  min: {
                    value: 1,
                    message: 'Enter 1 (one) or more',
                  },
                }),
              }}
              //@ts-ignore
              Error={!!errors?.cents_per_minute?.message?.length}
              ErrorMessage={errors.cents_per_minute?.message}
              enableAdornment
              dataTestId="subscription-overage"
            />
          </div>
          <div className="w-full">
            <CustomInput
              inputType="text"
              Label="Discount (%)"
              register={{
                ...register('discount', {
                  required: 'Please provide Subscription discount',
                  onChange: (e) => decimalOnChange(e, 'discount', setValue, 2),
                  onBlur: (e) => numberonBlur(e, 'discount', setValue),
                  valueAsNumber: true,
                  min: {
                    value: 0.0,
                    message: 'Enter 0 (zero) or more',
                  },
                }),
              }}
              //@ts-ignore
              Error={!!errors?.discount?.message?.length}
              ErrorMessage={errors.discount?.message}
              dataTestId="subscription-discount"
            />
          </div>
          <div className="w-full">
            <CustomInput
              inputType="text"
              Label="Corner Banner"
              register={{
                ...register('banner_text', {
                  required: 'Please provide Subscription Banner Text',
                  onBlur: (e: any) => onBlur(e, 'banner_text', setValue),
                }),
              }}
              //@ts-ignore
              Error={errors?.banner_text?.message?.length ? true : false}
              ErrorMessage={errors.banner_text?.message}
              dataTestId="subscription-corner-banner"
            />
          </div>

          <div className="flex h-14 min-w-44 items-end gap-2">
            <button
              className={`h-10 rounded-full px-3 py-1 text-center text-sm text-white ${
                isDirty || type === 'new' ? 'bg-blue-primary' : 'bg-gray-background-light'
              }`}
              type="submit"
              data-testid="subscription-add-update"
            >
              {type === SubscriptionsType.NEW ? 'Add' : 'Update'}
            </button>
            {type === SubscriptionsType.EXISTING && (
              <button
                className="bg-blue-primary h-10 rounded-full px-3 py-1 text-center text-sm text-white"
                onClick={remove}
                data-testid="subscription-remove"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default MemberPay;
